import { useCallback, useContext, useEffect, useState } from "react"
import { DoubleSide, FrontSide } from "three"
import { useAppDispatch } from '../../Common/_hooks/useAppDispatch';
import ModelElements from "../ModelElements"
import type { ProductModelProps } from "../ModelViewerProduct"
import ProductTextureContext from "../_contexts/ProductTextureContext"
import useGLB, { type HasStandardMaterial } from "../_hooks/useGLB"
import { EditActiveOption } from "../../UIData/_actions/DesignLabStoreActions"
import { applyFabricMaterialProfile } from '../fabricMaterialProfiles'

/* Static URLs let webpack discover every model while the active one remains data-driven. */
const MODEL_VARIANTS = {
    'long-scarf-flat': new URL('@resources/3d-models/glb/long-scarf-flat.glb', import.meta.url),
    'long-scarf-16x72': new URL('@resources/3d-models/glb/long-scarf-16x72.glb', import.meta.url),
    'long-scarf-10x45': new URL('@resources/3d-models/glb/long-scarf-10x45.glb', import.meta.url),
    'square-scarf-flat': new URL('@resources/3d-models/glb/square-scarf-flat.glb', import.meta.url),
    'square-scarf-16x16': new URL('@resources/3d-models/glb/square-scarf-16x16.glb', import.meta.url),
    'square-scarf-26x26': new URL('@resources/3d-models/glb/square-scarf-26x26.glb', import.meta.url),
    'square-scarf-36x36': new URL('@resources/3d-models/glb/square-scarf-36x36.glb', import.meta.url),
    'square-scarf-50x50': new URL('@resources/3d-models/glb/square-scarf-50x50.glb', import.meta.url),
} as const

type ScarfModelVariant = keyof typeof MODEL_VARIANTS

const isScarfModelVariant = (model:string|undefined):model is ScarfModelVariant => (
    model !== undefined && Object.prototype.hasOwnProperty.call(MODEL_VARIANTS, model)
)

export default function Scarf(props: ProductModelProps) {
    const dispatch = useAppDispatch();
    const [forceUpdate, setForceUpdate] = useState(0)
    const [, fxTexture] = useContext(ProductTextureContext)

    const optionFabric = props.options.get('fabric')
    const threadColor = props.options.get('thread-color') ?? 'black'
    const isLong = ['10x45', '16x72'].includes(props.variant)

    const fallbackModelVariant:ScarfModelVariant = isLong
        ? 'long-scarf-flat'
        : 'square-scarf-flat'
    const modelVariant = isScarfModelVariant(props.model)
        ? props.model
        : fallbackModelVariant

    const processMaterial = useCallback((object: HasStandardMaterial) => {
        object.material.toneMapped = false
        object.material.depthWrite = true
        object.material.transparent = true
        object.material.color.set(1, 1, 1)

        // For some reason three doesn't load the alpha in the right place
        if (!object.material.alphaMap) {
            object.material.alphaMap = object.material.map
            object.material.map = null
        }

        const objectName = typeof object.userData.name === 'string'
            ? object.userData.name
            : ''
        if (objectName.includes("Front")) {
            if (fxTexture) {
                object.material.map = fxTexture
                object.material.map.flipY = false
                object.material.map.needsUpdate = true
            }
            object.material.alphaToCoverage = false
            object.material.side = DoubleSide
            object.material.opacity = 0.95
        } else if (objectName.includes("Back")) {
            // Trying to help with Z-fighting
            object.material.polygonOffset = true
            object.material.polygonOffsetFactor = -0.5

            object.material.side = FrontSide
            object.material.alphaToCoverage = true
            object.material.opacity = 0.8
        }

        // Can't bake maps cause texel size would be too big so just scale a seamless one instead
        if (object.material.normalMap) {
            object.material.normalMap.repeat.x = 6
            object.material.normalMap.repeat.y = isLong ? 1.333 : 6
            object.material.normalScale.x = 2
            object.material.normalScale.y = 2
        }
        if (object.material.alphaMap) {
            object.material.alphaMap.repeat.x = 6
            object.material.alphaMap.repeat.y = isLong ? 1.333 : 6
        }
        if (object.material.metalnessMap) {
            object.material.metalnessMap.repeat.x = 6
            object.material.metalnessMap.repeat.y = isLong ? 1.333 : 6
        }
        if (object.material.roughnessMap) {
            object.material.roughnessMap.repeat.x = 6
            object.material.roughnessMap.repeat.y = isLong ? 1.333 : 6
        }
        applyFabricMaterialProfile(object.material, optionFabric)
    }, [fxTexture, isLong, optionFabric])

    const model = useGLB(MODEL_VARIANTS[modelVariant])

    // Keep the historical Design Lab size selection synchronized with the
    // model selected by the preview contract.
    useEffect(() => {
        dispatch(EditActiveOption("size", props.variant));
    }, [dispatch, props.variant])

    // KHR variant materials load lazily from the cached parser. The GLB
    // instance facade converts them to viewer-owned materials before this
    // product-specific processing callback is allowed to mutate them.
    useEffect(() => {
        const variants = model.variants
        if(!variants) return

        const controller = new AbortController()
        void variants.select(optionFabric, processMaterial).then(() => {
            if(!controller.signal.aborted) {
                setForceUpdate(current => current + 1)
            }
        })

        return () => {
            controller.abort()
        }
    }, [
        model.variants,
        optionFabric,
        processMaterial,
        props.variant,
        threadColor,
    ])

    return <ModelElements
        forceUpdate={`${optionFabric ?? ''}${threadColor}${forceUpdate}`}
        scene={model.scene}
    />
}
